// Per-class look of the weapon effects: how big the muzzle flash is, what the gun ejects and when,
// and how often a continuously firing gun shows a tracer. Pure data, no three.js.

/** What leaves the ejection port: a brass case, a shotgun hull or nothing (revolvers keep their cases). */
export type ShellKind = "none" | "brass" | "rifle" | "hull";

export interface WeaponFxProfile {
  /** Scale of the muzzle flash (1 = assault rifle). */
  flash: number;
  shell: ShellKind;
  /** Seconds after the shot at which the case leaves the gun (bolt and pump actions eject on the cycle). */
  shellDelay: number;
}

const DEFAULT: WeaponFxProfile = { flash: 1, shell: "rifle", shellDelay: 0 };

/** Keyed by the catalog class slug ("smg", "rifle", "burst_rifle", ...). */
const PROFILES: Readonly<Record<string, WeaponFxProfile>> = {
  smg: { flash: 0.8, shell: "brass", shellDelay: 0 },
  rifle: DEFAULT,
  burst_rifle: DEFAULT,
  shotgun: { flash: 1.5, shell: "hull", shellDelay: 0.38 },
  marksman: { flash: 1.25, shell: "rifle", shellDelay: 0 },
  sniper: { flash: 1.8, shell: "rifle", shellDelay: 0.45 },
  pistol: { flash: 0.85, shell: "brass", shellDelay: 0 },
  machine_pistol: { flash: 0.7, shell: "brass", shellDelay: 0 },
  revolver: { flash: 1.45, shell: "none", shellDelay: 0 },
};

export function weaponFxProfile(weaponClass: string): WeaponFxProfile {
  return PROFILES[weaponClass] ?? DEFAULT;
}

/**
 * Of the shots of one continuous burst the player's own view draws every n-th tracer: a tracer
 * on every round of a 700 rpm gun would read as one beam. The first round always shows one.
 */
export function tracerEvery(fireIntervalSec: number): number {
  if (fireIntervalSec >= 0.12) return 1;
  if (fireIntervalSec >= 0.075) return 2;
  return 3;
}
